/** Shared formatting helpers. */

export function bytes(n: number | undefined): string {
  if (!n) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

/**
 * A count as a person reads it, grouped the locale's way: 25,000. The pages
 * print every count through here, so they group alike; the trial's cap was
 * formatted inline once (D16).
 */
export function count(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return n.toLocaleString()
}

export function dateTime(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function dateOnly(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function relativeDays(iso: string | undefined): string {
  if (!iso) return ''
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000)
  if (Number.isNaN(days)) return ''
  if (days === -1) return 'yesterday'
  if (days < 0) return `${Math.abs(days)} days ago`
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days < 365) return `in ${days} days`
  return `in ${(days / 365).toFixed(1)} years`
}

/** Turns "participant.consented" into "Participant consented". */
export function eventLabel(type: string): string {
  if (type === 'envelope.legal_hold') return 'Dispute lock changed'
  const text = type.replace(/[._]/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Buyer-facing name for the `legal_hold` plan feature and envelope flag. */
export const disputeLockLabel = 'Dispute lock'

/**
 * How long a document is kept once it is finished: "1 year", "7 years", or in days
 * when the period is not a whole number of years ("90 days"). The envelope
 * page divided by 365 and always said "years", so one year was "1 years" and
 * an organization that keeps 90 days was told "0 years".
 */
export function retention(days: number | undefined | null): string {
  if (days == null || !Number.isFinite(days) || days <= 0) return '—'
  if (days % 365 === 0) {
    const years = days / 365
    return `${years} year${years === 1 ? '' : 's'}`
  }
  return `${days} day${days === 1 ? '' : 's'}`
}

export function shortHash(hash: string | undefined, chars = 12): string {
  if (!hash) return '—'
  return hash.length <= chars * 2 ? hash : `${hash.slice(0, chars)}…${hash.slice(-4)}`
}

export function formatUsd(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

export function planLabel(plan: string): string {
  switch (plan) {
    case 'solo':
      return 'Solo'
    case 'team':
      return 'Team'
    case 'business':
      return 'Business'
    case 'enterprise':
      return 'Enterprise'
    default:
      return plan
  }
}
